import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import AppContext from "../../../context/Appcontext";
import "./../../../assets/css/student/Signup/signup.css";
import signupImage from "../../../assets/images/mySignupImage.png";

// ─── SVG Icons ────────────────────────────────────────────────
const IconEyeOpen = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const IconEyeClosed = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);
// ──────────────────────────────────────────────────────────────

const Signup = () => {
  const navigate = useNavigate();
  const { baseUrl } = useContext(AppContext);

  // ── Page loader state ──
  const [pageLoading, setPageLoading] = useState(true);

  // ── Form fields ──
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreed, setAgreed] = useState(false);

  // ── UI states ──
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // ── Auto-login spinner after signup ──
  const [showAutoLoginSpinner, setShowAutoLoginSpinner] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setPageLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  // ── Auto-login after successful signup ──
  const autoLogin = (userEmail, userPassword) => {
    setShowAutoLoginSpinner(true);

    const formdata = new FormData();
    formdata.append("email", userEmail);
    formdata.append("password", userPassword);

    axios
      .post(`${baseUrl}/student/login`, formdata, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        const student = response.data.student;

        localStorage.setItem("studentData", JSON.stringify(student));
        localStorage.setItem("firstName", student?.firstName || "");
        localStorage.setItem("lastName", student?.lastName || "");
        localStorage.setItem("email", userEmail);
        localStorage.setItem("role", "student");

        setTimeout(() => {
          setShowAutoLoginSpinner(false);
          navigate("/student/dashboard");
        }, 2000);
      })
      .catch(() => {
        setShowAutoLoginSpinner(false);
        setErrorMessage("Auto-login failed. Please login manually.");
        navigate("/login");
      });
  };

  // ── Signup submit ──
  const handleSignup = (event) => {
    event.preventDefault();
    setErrorMessage("");

    // Basic validations
    if (!firstName.trim() || !lastName.trim()) {
      setErrorMessage("Please enter your full name.");
      return;
    }
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) {
      setErrorMessage("Please enter a valid email address.");
      return;
    }
    if (password.length < 6) {
      setErrorMessage("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      return;
    }
    if (!agreed) {
      setErrorMessage("Please agree to the Terms & Privacy Policy.");
      return;
    }

    setIsLoading(true);

    axios
      .post(`${baseUrl}/student/signup`, {
        firstName,
        lastName,
        email,
        password,
        confirmPassword,
      })
      .then(() => {
        setIsLoading(false);
        // Auto-login the student right after signup
        autoLogin(email, password);
      })
      .catch((error) => {
        setIsLoading(false);
        const data = error.response?.data;

        // Agar email already registered ho
        if (data?.message === "This email is already registered.") {
          Swal.fire({
            title: "Account Already Exists!",
            text: "This email is already registered. Would you like to login instead?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#7d2937",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Yes, Login",
            cancelButtonText: "Cancel",
          }).then((result) => {
            if (result.isConfirmed) navigate("/login");
          });
          return;
        }

        // Agar backend se multiple errors aayein
        if (data?.errors) {
          setErrorMessage(Object.values(data.errors).flat().join(", "));
        } else {
          setErrorMessage(data?.message || "Signup failed. Please try again.");
        }
      });
  };

  // ─── Auto-login spinner overlay ───
  if (showAutoLoginSpinner) {
    return (
      <div className="sp-spinner-overlay">
        <div className="sp-spinner-content">
          <div className="sp-spinner-ring" />
          <h3 className="sp-spinner-text">
            Welcome! Setting up your dashboard…
          </h3>
          <p className="sp-spinner-sub">Please wait a moment</p>
        </div>
      </div>
    );
  }

  // ─── Page loader ───
  if (pageLoading) {
    return (
      <div className="sp-loader">
        <div className="sp-page-spinner" />
      </div>
    );
  }

  // ─── Main UI ───
  return (
    <div className="sp-page">
      {/* Left — Form */}
      <div className="sp-left">
        <div className="sp-card">
          <div className="sp-header">
            <h1 className="sp-title">Create Account</h1>
            <p className="sp-subtitle">
              Start your learning journey with <strong>TechMentor Hub</strong>
            </p>
          </div>

          <form onSubmit={handleSignup} noValidate>
            {/* First Name & Last Name */}
            <div className="sp-name-row">
              <div className="sp-field">
                <label className="sp-label">First Name</label>
                <input
                  className="sp-input"
                  type="text"
                  placeholder="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  autoComplete="given-name"
                />
              </div>
              <div className="sp-field">
                <label className="sp-label">Last Name</label>
                <input
                  className="sp-input"
                  type="text"
                  placeholder="Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  autoComplete="family-name"
                />
              </div>
            </div>

            {/* Email */}
            <div className="sp-field">
              <label className="sp-label">Email</label>
              <input
                className="sp-input"
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </div>

            {/* Password */}
            <div className="sp-field">
              <label className="sp-label">Password</label>
              <div className="sp-input-wrap">
                <input
                  className="sp-input"
                  type={showPass ? "text" : "password"}
                  placeholder="Minimum 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="sp-eye"
                  onClick={() => setShowPass((previous) => !previous)}
                  aria-label="Toggle password visibility"
                >
                  {showPass ? <IconEyeClosed /> : <IconEyeOpen />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div className="sp-field">
              <label className="sp-label">Confirm Password</label>
              <div className="sp-input-wrap">
                <input
                  className="sp-input"
                  type={showConfirm ? "text" : "password"}
                  placeholder="Re-enter your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="sp-eye"
                  onClick={() => setShowConfirm((previous) => !previous)}
                  aria-label="Toggle confirm password visibility"
                >
                  {showConfirm ? <IconEyeClosed /> : <IconEyeOpen />}
                </button>
              </div>
            </div>

            {/* Terms & Conditions */}
            <label className="sp-terms-row">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
              />
              <span className="sp-check-box" aria-hidden="true" />
              <span className="sp-terms-label">
                I agree to{" "}
                <a href="/terms" onClick={(e) => e.stopPropagation()}>
                  Terms &amp; Privacy Policy
                </a>
              </span>
            </label>

            {/* Error Message */}
            {errorMessage && (
              <div className="sp-error" role="alert">
                {errorMessage}
              </div>
            )}

            {/* Submit Button */}
            <button type="submit" className="sp-btn" disabled={isLoading}>
              {isLoading ? (
                <>
                  <span className="sp-btn-spinner" aria-hidden="true" />{" "}
                  Creating…
                </>
              ) : (
                "SIGN UP"
              )}
            </button>
          </form>

          <p className="sp-login-row">
            Already have an account?
            <button
              type="button"
              className="sp-login-link"
              onClick={() => navigate("/login")}
            >
              Login
            </button>
          </p>
        </div>
      </div>

      {/* Right — Image */}
      <div className="sp-right">
        <button className="sp-back-btn" onClick={() => navigate("/")}>
          Back
        </button>
        <div className="sp-illus-wrap">
          <img src={signupImage} alt="Student learning" className="sp-illus" />
        </div>
        <div className="sp-right-inner">
          <p className="sp-rt-top">Welcome to</p>
          <h2 className="sp-rt-title">TechMentor Hub</h2>
          <p className="sp-rt-sub">
            Create your account and start your learning journey.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;
